<template>
	<div :class="block ? 'flex w-full flex-col' : 'inline-flex flex-col'">
		<h2 v-if="!inline">
			<button
				type="button"
				class="flex items-center justify-between w-full p-5 font-medium select-none rtl:text-right text-gray-500 border border-b-0 border-gray-200 rounded-t-xl focus:ring-2 focus:ring-primary dark:focus:ring-gray-800 dark:border-gray-700 dark:text-gray-400 gap-3"
				:class="isArchived ? 'hover:bg-red-200 dark:hover:bg-red-800 ' : 'hover:bg-gray-100 dark:hover:bg-gray-800'"
				:style="{backgroundColor: isArchived ? '#FDDADA' : ''}"
				@click="collapsed = !collapsed"
			>
				<slot name="title" :collapsed="collapsed">
					<span>{{ title }}</span>
				</slot>
				<AppIcon :icon="collapsed ? 'chevron-down' : 'chevron-up'"/>
			</button>
		</h2>
		<a v-else :class="block ? 'flex w-full items-center' : 'inline-flex items-center'" @click="collapse">
			<slot name="title" :collapsed="collapsed">
				<div>{{ title }}
					<AppIcon
						:icon="collapsed ? 'caret-down' : 'caret-up'"/>
				</div>
			</slot>
		</a>
		<Transition>
			<div
				v-show="!collapsed"
			>
				<div
					class="border-b-0 border-gray-200 dark:border-gray-700 dark:bg-gray-900"
					:class="{'border p-2': !inline}"
				>
					<slot/>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
const props = withDefaults(
	defineProps<{
		title?: string;
		renderCollapsed?: boolean;
		isArchived?: boolean;
		inline?: boolean;
		// Opt-in: sync collapsed state from outside (e.g. a collapse-all toggle). Default-off.
		forceCollapse?: boolean;
		// Opt-in: render as a full-width block instead of shrink-to-content inline-flex. Default-off.
		block?: boolean;
	}>(),
	{
		renderCollapsed: true,
		isArchived: false,
		inline: false,
	},
);

function collapse() {
	collapsed.value = !collapsed.value;
	emit("collapse");
}

const emit = defineEmits(["collapse"]);

const collapsed = ref(true);

// Non-immediate: the onMounted renderCollapsed seeding still wins on first render
watch(
	() => props.forceCollapse,
	(v) => {
		if (typeof v === "boolean") collapsed.value = v;
	},
);

onMounted(() => {
	collapsed.value = props.renderCollapsed;
});
</script>

<style scoped>
.v-enter-active {
	transition: opacity 0.1s ease-in;
}

.v-enter-from, .v-leave-to {
	opacity: 0;
}
</style>
